/** Browser client for the authenticated, latest-only Operations MCP gateway. */
import { MCP_PROTOCOL_VERSION } from '../../../../shared/mcpProtocol'
import { apiFetch } from './apiClient'

/**
 * Prefetch the official MCP SDK in parallel with a page that needs it
 * (Cockpit, GitRepositories). The SDK never rides the entry chunk: it loads
 * on demand inside connect(), or here ahead of time while the route chunk
 * arrives.
 */
let sdkPrefetch = null
export function prefetchMcpSdk() {
  sdkPrefetch ??= import('@modelcontextprotocol/client').catch((error) => {
    sdkPrefetch = null
    throw error
  })
  return sdkPrefetch
}

/**
 * The console's own MCP lane, not `/mcp`. When a customer's MCP sign-in is on,
 * Cloudflare Access fronts exactly `<api host>/mcp` for external OAuth clients
 * and the appliance runner. A browser carries neither an Access session for
 * the API host nor a service token, so on that path it receives Access's
 * sign-in page instead of the gateway and the official client reports
 * "the server did not offer pinned protocol version" — for every MCP-backed
 * console feature, instantly. The worker serves the same gateway here
 * (routes/handler.ts PORTAL_MCP_PATH), under portal authentication only.
 */
export const PORTAL_MCP_ENDPOINT = '/portal/mcp'
const MCP_ENDPOINT = PORTAL_MCP_ENDPOINT

/**
 * Own the one browser-lifetime connection. The official client emits the
 * 2026 per-request envelope and transport headers; apiFetch remains the sole
 * owner of portal authentication, vanity-host discovery, and 401 refresh.
 */
class PortalMcpConnection {
  clientPromise = null

  getClient() {
    if (!this.clientPromise) {
      this.clientPromise = this.connect().catch((error) => {
        this.clientPromise = null
        throw error
      })
    }
    return this.clientPromise
  }

  async connect() {
    const { Client, StreamableHTTPClientTransport } = await prefetchMcpSdk()
    const client = new Client(
      { name: 'operations-portal', version: '1.0.0' },
      {
        versionNegotiation: { mode: { pin: MCP_PROTOCOL_VERSION } },
        enforceStrictCapabilities: true,
      },
    )
    const transport = new StreamableHTTPClientTransport(
      new URL(MCP_ENDPOINT, window.location.origin),
      {
        // The transport supplies the complete RequestInit. The endpoint is
        // deliberately resolved by apiFetch so runtime vanity discovery and
        // the authenticated retry path cannot be bypassed.
        // The page declares itself. The gateway caps inline results at 16 KB
        // for model clients and offloads the rest to scratch for them to page;
        // a browser has no context window, so it gets results inline.
        fetch: (_url, init) => {
          // The transport hands over a Headers instance; spreading one loses
          // every entry, including the protocol version.
          const headers = new globalThis.Headers(init?.headers || {})
          headers.set('X-Operations-Client', 'portal')
          return apiFetch(MCP_ENDPOINT, { ...init, headers })
        },
      },
    )
    await client.connect(transport)
    return client
  }

  async run(method, operation) {
    try {
      return await operation(await this.getClient())
    } catch (cause) {
      const detail = cause instanceof Error ? cause.message : String(cause)
      throw new Error(`MCP ${method} failed: ${detail}`, { cause })
    }
  }
}

const connection = new PortalMcpConnection()

export function mcpServerInfo() {
  return connection.run('server/discover', async (client) => client.getServerVersion() || null)
}

export function mcpListTools() {
  return connection.run('tools/list', (client) => client.listTools())
}

export function mcpCallTool(name, args) {
  return connection.run('tools/call', (client) => client.callTool({ name, arguments: args }))
}

/** Read one resource (the interactive view page). Refused when the server serves none to this person. */
export function mcpReadResource(uri) {
  return connection.run('resources/read', (client) => client.readResource({ uri }))
}

/** Unwrap an execute envelope. Partial data is never presented as complete. */
function readExecuteEnvelope(result) {
  const text = result?.content?.[0]?.text ?? ''
  let envelope
  try {
    envelope = JSON.parse(text)
  } catch {
    throw new Error('execute returned a non-JSON envelope')
  }
  logExecuteTimings(envelope)
  if (envelope?.state !== 'completed') {
    throw new Error(
      envelope?.error?.message
      || envelope?.error
      || `execute ${envelope?.state || 'failed'}`,
    )
  }
  if (envelope.truncated) {
    // The gateway's own warning says what happened and how big the result
    // was; that is the error, not a sentence invented here.
    const serverSaid = Array.isArray(envelope.warnings) && envelope.warnings.length
      ? envelope.warnings.join(' ')
      : `execute result was truncated (${envelope.bytes ?? '?'} bytes)`
    throw new Error(serverSaid)
  }
  return envelope.result
}

/**
 * Run a code-mode read and return its value. Reserved for work that genuinely
 * needs several calls with logic between them; a page that knows the method it
 * wants uses opsCall, which runs on the host instead of in a sandbox.
 */
export async function mcpExecute(code) {
  return readExecuteEnvelope(await mcpCallTool('execute', { code }))
}

/**
 * In-flight identical reads, keyed by the four values that define a call.
 *
 * Independent panels ask for the same thing at the same moment — the action
 * lane and the supervisor watch both refresh actions, a focus event and a
 * heartbeat both sync runs. Each duplicate was a second round trip for a
 * byte-identical answer. Callers share the pending promise instead; the entry
 * is dropped as soon as it settles, so this coalesces concurrent reads and
 * caches nothing.
 */
const inFlightOpsCalls = new Map()

/**
 * Run ONE named `ops` method against a case and return its value.
 *
 * This is how the page reads. It names the method instead of authoring a
 * program that calls it, so the gateway runs it on the host: no sandbox
 * worker, no selector write, and the case is active for this call only.
 */
export function opsCall(caseId, area, method, args) {
  const key = JSON.stringify([caseId ?? null, area, method, args ?? null])
  const running = inFlightOpsCalls.get(key)
  if (running) return running

  const pending = connection
    .run('tools/call', (client) => client.callTool({
      name: 'execute',
      arguments: {
        ...(caseId ? { caseId } : {}),
        call: { area, method, ...(args === undefined ? {} : { args }) },
      },
    }))
    .then(readExecuteEnvelope)
  inFlightOpsCalls.set(key, pending)
  const forget = () => { if (inFlightOpsCalls.get(key) === pending) inFlightOpsCalls.delete(key) }
  pending.then(forget, forget)
  return pending
}

function logExecuteTimings(envelope) {
  try {
    if (!import.meta.env?.DEV) return
    const timings = envelope?.timings
    if (!Array.isArray(timings) || timings.length === 0) return
    const total = timings.reduce((sum, timing) => sum + (timing.ms || 0), 0)
    const parts = timings
      .map((timing) =>
        `${timing.op} ${timing.ms}ms${timing.calls > 1 ? `×${timing.calls}` : ''}`,
      )
      .join('  ·  ')
    console.debug(`[mcp execute] ${total}ms server · ${parts}`)
  } catch {
    // Development diagnostics never affect the data path.
  }
}

export async function mcpPrompts() {
  const out = await connection.run('prompts/list', (client) => client.listPrompts())
  return Array.isArray(out?.prompts) ? out.prompts : []
}

export async function mcpRenderPrompt(name, args) {
  const out = await connection.run(
    'prompts/get',
    (client) => client.getPrompt({ name, arguments: args || {} }),
  )
  const text = (out?.messages ?? [])
    .map((message) =>
      typeof message.content === 'string'
        ? message.content
        : message.content?.text || '',
    )
    .filter(Boolean)
    .join('\n\n')
  return { description: out?.description, text }
}
